import { NavLinkItem } from 'motion-components';
import React, { FC, useMemo } from 'react';
import { Nav, NavDropdown } from 'react-bootstrap';
import { generatePath } from 'react-router-dom';
import { getRouteByName } from 'app/routes';
import { useRootStore } from 'store';
import { t } from 'i18next';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useUserPermissions } from 'shared-pages/hooks/user-permissions';
import { isAfisCyprus, isMedicycle, isRebatteryLiion } from 'shared-pages/common/constants';

const regionsTitle = isAfisCyprus || isMedicycle ? 'Επαρχίες' : 'Περιφέρειες';

export const menuItemsNames = {
  FILES: 'Αρχεία',
  MATERIALS: 'Απόβλητα',
  CONTAINER_TYPES: 'Περιέκτες / Συσκευασίες',
  NOMOI: 'Νομοί',
  REGIONS: regionsTitle,
  SUBREGIONS: 'Περιφερειακές Ενότητες',
  USER_ROLES: 'Ρόλοι Χρηστών',
  USERS: 'Χρήστες',
  VEHICLES: 'Οχήματα',
  DRIVERS: 'Οδηγοί',
  EDOC_SERIES: 'Σειρές Αρίθμησης',
  WASTE_RECEPTION_POINTS: 'Ανακυκλωτές',
  WASTE_RECEPTION_POINTS_DA: 'Δευτερογενείς Αποθήκες',
  COLLECTOR: 'Συλλέκτης',
  COLLECTORS: 'Συλλέκτες',
  COLLECTION_POINT_CATEGORIES: 'Κατηγορίες Σημείων',
  COLLECTION_POINTS: 'Σημεία Συλλογής',
  COLLECTION_POINT: 'Σημείο Συλλογής',
  COLLECTION_POINTS_STATS: 'Στατιστικά Σημείων Συλλογής',
  COLLECTION_POINT_MAP: 'Χάρτης Σημείων Συλλογής',
  CONTAINERS_QRCODES: 'QR code Συσκευασιών',
  COLLECTION_POINT_REQUESTS: 'Αιτήσεις Νέων Σημείων',
  CONTAINER_REQUESTS: 'Παραγγελίες Συσκευασιών',

  ROUTES: 'Δρομολόγια',
  COLLECTIONS: 'Συλλογές',
  COLLECTIONS_ORDERS: 'Αιτήσεις Συλλογής',
  COLLECTIONS_ORDERS_STATUSES: 'Αιτήσεις ανά Κατάσταση',
  INTERNALTRANSACTIONS: 'Εσωτερικές Διακινήσεις',
  WASTERECEPTIONPOINT_TRANSACTIONS: 'Κινήσεις Αποθήκης',
  DRIVER_APP_ENTRIES: 'Καταχωρήσεις Οδηγών',
  IDOCS: 'Έντυπα Αναγνώρισης',
  IDOCS4FINALDESTINATION: 'Τελική Διάθεση EAEA',
  IDOCS4WASTERECEPTION: 'Έντυπα προς Παραλαβή',
  DISP_NOTES: 'Δελτία Αποστολής',

  REPORTS: 'Αναφορές',
  COLLECTIONS_REP_DGRM: 'Ανάλυση Συλλογών',
  COLLECTIONS_PIVOT: 'Pivot Συλλογών',

  ADMINISTRATION: 'Διαχείριση',
  AUDIT_TRAIL: 'Audit Trail',
  MAILS_REPO: 'Emails',
  LOG_BOOK: 'Βιβλίο Συμβάντων',
  USERS_STATS: 'Στατιστικά Χρηστών',
  PARAMS: 'Παράμετροι',

  CONTRIBUTIONS: 'Εισφορές',
  CONTRIBUTIONSHISTORY: 'Ιστορικό Δηλώσεων',
  CONTRIBUTORS: 'Υπόχρεοι',
};

const MyNavLinkItem = ({ text, routeName, disabled = false }) => {
  const { getUserCanView } = useUserPermissions();

  if (typeof routeName === 'object') {
    const rtName = routeName.routeName;
    const to = { ...routeName, pathname: getRouteByName(rtName) };
    const canView = getUserCanView(rtName);
    if (!canView || disabled) {
      return (
        <a className="ps-3 nav-link disabled" style={{ color: 'gray!important' }} href="/">
          {text}
        </a>
      );
    }
    return <NavLinkItem text={text} to={to} enable={canView} />;
  } else {
    const canView = getUserCanView(routeName);
    if (!canView || disabled) {
      return <a className="ps-3 nav-link disabled">{text}</a>;
    }
    return <NavLinkItem text={text} to={getRouteByName(routeName)} enable={canView} />;
  }
};

type MainMenuProps = {
  menuItems?: typeof MI;
};

const MI = menuItemsNames;

export const commonMenuItems = {
  _________: <NavDropdown.Divider />,
  Materials: <MyNavLinkItem text={t(MI.MATERIALS)} routeName={'Materials'} />,
  ContainerTypes: <MyNavLinkItem text={t(MI.CONTAINER_TYPES)} routeName={'ContainerTypes'} />,

  Regions: <MyNavLinkItem text={t(MI.REGIONS)} routeName={'Regions'} />,
  Subregions: <MyNavLinkItem text={t(MI.SUBREGIONS)} routeName={'Subregions'} />,
  Nomoi: <MyNavLinkItem text={t(MI.NOMOI)} routeName={'Nomoi'} />,
  Vehicles: <MyNavLinkItem text={t(MI.VEHICLES)} routeName="Vehicles" />,
  Drivers: <MyNavLinkItem text={t(MI.DRIVERS)} routeName="Drivers" />,
  EdocSeries: <MyNavLinkItem text={t(MI.EDOC_SERIES)} routeName="Series" />,
  Collectors: <MyNavLinkItem text={t(MI.COLLECTORS)} routeName="Collectors" />,
  WasteReceptionPoints: (
    <MyNavLinkItem text={t(MI.WASTE_RECEPTION_POINTS)} routeName="WasteReceptionPoints" />
  ),
  WasteReceptionPointsDa: (
    <MyNavLinkItem text={t(MI.WASTE_RECEPTION_POINTS_DA)} routeName="WasteReceptionPointsDa" />
  ),
  Users: <NavLinkItem text={t(MI.USERS)} to={getRouteByName('Users')} enable={true} />,
  Collectionpoints: (
    <MyNavLinkItem
      text={t(MI.COLLECTION_POINTS)}
      routeName={{ routeName: 'Collectionpoints', state: { app: 'RE-BATTERY' } }}
    />
  ),
  CollectionpointsStats: (
    <MyNavLinkItem
      text={t(MI.COLLECTION_POINTS_STATS)}
      routeName={{ routeName: 'CPStats', state: {} }}
    />
  ),
  CollectionpointsMap: (
    <MyNavLinkItem
      text={t(MI.COLLECTION_POINT_MAP)}
      routeName={{ routeName: 'CPMap', state: { app: 'RE-BATTERY' } }}
      disabled={false}
    />
  ),
  ContainersQrCodes: (
    <MyNavLinkItem text={t(MI.CONTAINERS_QRCODES)} routeName={{ routeName: 'ContainersQrCodes' }} />
  ),

  CollectionpointCategories: (
    <MyNavLinkItem text={t(MI.COLLECTION_POINT_CATEGORIES)} routeName={'CpGroups'} />
  ),
  CollectionpointRequests: (
    <MyNavLinkItem text={t(MI.COLLECTION_POINT_REQUESTS)} routeName={'CollectionpointRequests'} />
  ),
  ContainerRequests: (
    <MyNavLinkItem text={t(MI.CONTAINER_REQUESTS)} routeName={'ContainerOrders'} />
  ),
  CollectionOrders: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.COLLECTIONS_ORDERS)}
      routeName={{ routeName: 'Orders', state: { app: 'WBMANAGER' } }}
    />
  ),
  CollectionOrdersStatuses: (
    <MyNavLinkItem text={t(MI.COLLECTIONS_ORDERS_STATUSES)} routeName={'OrdersStatuses'} />
  ),
  Routes: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.ROUTES)}
      routeName={{ routeName: 'Routes', state: { app: 'WBMANAGER' } }}
    />
  ),
  Collections: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.COLLECTIONS)}
      routeName={{ routeName: 'Collections', state: { app: 'WBMANAGER' } }}
    />
  ),
  InternalTransactions: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.INTERNALTRANSACTIONS)}
      routeName={{ routeName: 'ITrans', state: { app: 'WBMANAGER' } }}
    />
  ),
  WrsTransactions: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.WASTERECEPTIONPOINT_TRANSACTIONS)}
      routeName={{ routeName: 'WrsTrans', state: { app: 'WBMANAGER' } }}
    />
  ),
  DriverAppEntries: (
    <MyNavLinkItem text={t(MI.DRIVER_APP_ENTRIES)} routeName="DriversAppEntries" disabled={false} />
  ),
  DispNotes: (
    <MyNavLinkItem
      text={t(MI.DISP_NOTES)}
      routeName={{ routeName: 'DispNotesGrid', state: { company: 'FOTOKIKLOSI' } }}
      disabled={true}
    />
  ),
  Idocs: <MyNavLinkItem text={t(MI.IDOCS)} routeName={'Idocs'} />,
  Idocs4FinalDest: (
    <MyNavLinkItem text={t(MI.IDOCS4FINALDESTINATION)} routeName={'Idocs4Finaldest'} />
  ),
  Idocs4WasteReception: (
    <MyNavLinkItem text={t(MI.IDOCS4WASTERECEPTION)} routeName={'Idocs4WasteReception'} />
  ),
  CollectionsRepDgrmReport: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.COLLECTIONS_REP_DGRM)}
      routeName={{ routeName: 'CollectionsRepDgrm', state: { company: '' } }}
    />
  ),
  CollectionsPivotReport: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.COLLECTIONS_PIVOT)}
      routeName={{ routeName: 'CollectionsPivot', state: { company: '' } }}
    />
  ),

  Audittrail: (
    <NavLinkItem text={t(MI.AUDIT_TRAIL)} to={getRouteByName('AuditTrails')} enable={true} />
  ),
  Logbook: <NavLinkItem text={t(MI.LOG_BOOK)} to={getRouteByName('Logs')} enable={true} />,
  Mails: <NavLinkItem text={t(MI.MAILS_REPO)} to={getRouteByName('Mails')} enable={true} />,
  UsersStats: (
    <NavLinkItem text={t(MI.USERS_STATS)} to={getRouteByName('UsersStats')} enable={true} />
  ),
  Params: <NavLinkItem text={t(MI.PARAMS)} to={getRouteByName('Params')} enable={true} />,

  Contributions: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.CONTRIBUTIONS)}
      routeName={{ routeName: 'Contributions', state: {} }}
    />
  ),
  ContributionsHistory: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.CONTRIBUTIONSHISTORY)}
      routeName={{ routeName: 'ContributionsHistory', state: {} }}
    />
  ),
  Contributors: (
    <MyNavLinkItem
      disabled={false}
      text={t(MI.CONTRIBUTORS)}
      routeName={{ routeName: 'Contributors', state: {} }}
    />
  ),
};
